import { Component, type ErrorInfo, type ReactNode } from "react";

/** Optional crash reporting (VITE_SENTRY_DSN) + a friendly error boundary that never leaves the user on a blank screen. */
export async function initMonitoring(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;
  const Sentry = await import("@sentry/react");
  Sentry.init({ dsn, environment: import.meta.env.MODE, release: import.meta.env.VITE_APP_VERSION as string | undefined, tracesSampleRate: 0.05, sendDefaultPii: false });
}

export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ui] uncaught", error, info.componentStack);
    void import("@sentry/react").then((S) => S.captureException(error, { extra: { componentStack: info.componentStack } })).catch(() => undefined);
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="grid min-h-full place-items-center bg-ground p-6 text-center">
        <div className="max-w-sm rounded-lg border border-line bg-surface p-6">
          <div className="font-display text-lg font-bold text-navy">Une erreur est survenue</div>
          <p className="mt-2 text-sm text-ink-2">Vos données locales sont conservées. Rechargez l'application ; si le problème persiste, signalez-le à la Direction.</p>
          <p className="mt-2 break-all font-mono text-[11px] text-ink-3">{this.state.error.message}</p>
          <button type="button" onClick={() => window.location.reload()} className="mt-4 h-11 w-full rounded-md bg-navy text-sm font-semibold text-white">Recharger</button>
        </div>
      </div>
    );
  }
}
